import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { professionalsAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Loader2, Upload, X, AlertCircle, CheckCircle, Pause, Play } from 'lucide-react';

const SPECIALTIES = [
  'Álcool', 'Nicotina', 'Drogas', 'Emoções', 'Jogos e Apostas',
  'Transtornos Alimentares', 'Dependência Sexual', 'Geral'
];

const PROFESSIONS = [
  { value: 'psicologo', label: 'Psicólogo(a)' },
  { value: 'psiquiatra', label: 'Psiquiatra' },
  { value: 'terapeuta_holistico', label: 'Terapeuta Holístico(a)' },
  { value: 'educador_fisico', label: 'Educador(a) Físico(a)' },
  { value: 'massoterapeuta', label: 'Massoterapeuta' },
  { value: 'nutricionista', label: 'Nutricionista' },
  { value: 'assistente_social', label: 'Assistente Social' },
  { value: 'outro', label: 'Outro' },
];

const REGISTRY_TYPES = [
  { value: 'CRP', label: 'CRP (Psicologia)' },
  { value: 'CRM', label: 'CRM (Medicina)' },
  { value: 'CRT', label: 'CRT (Terapias)' },
  { value: 'CREF', label: 'CREF (Ed. Física)' },
  { value: 'CRN', label: 'CRN (Nutrição)' },
  { value: 'CRESS', label: 'CRESS (Serviço Social)' },
  { value: 'OTHER', label: 'Outro' },
];

const APPROACHES = [
  'TCC (Terapia Cognitivo-Comportamental)',
  'Psicanálise',
  'Terapia Familiar',
  'Entrevista Motivacional',
  'Terapia Humanista',
  'Gestão de Estresse',
  'Mindfulness',
  'Outra'
];

const MODALITIES = [
  { value: 'online', label: 'Online' },
  { value: 'presencial', label: 'Presencial' },
  { value: 'hibrido', label: 'Híbrido' },
];

const LANGUAGES = [
  { value: 'pt-BR', label: 'Português' },
  { value: 'en', label: 'Inglês' },
  { value: 'es', label: 'Espanhol' },
  { value: 'fr', label: 'Francês' },
];

export function ProfessionalProfile() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [errors, setErrors] = useState({});
  
  // Form state
  const [displayName, setDisplayName] = useState('');
  const [profession, setProfession] = useState('');
  const [registryType, setRegistryType] = useState('');
  const [registryCode, setRegistryCode] = useState('');
  const [registryUf, setRegistryUf] = useState('');
  const [bio, setBio] = useState('');
  const [specialties, setSpecialties] = useState([]);
  const [approaches, setApproaches] = useState([]);
  const [modalities, setModalities] = useState([]);
  const [languages, setLanguages] = useState(['pt-BR']);
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('BR');
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [yearsExperience, setYearsExperience] = useState('');
  const [website, setWebsite] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [instagram, setInstagram] = useState('');
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await professionalsAPI.getMyProfile();
      const data = response.data;
      setProfile(data);
      
      // Populate form
      setDisplayName(data.display_name || '');
      setProfession(data.profession || '');
      // Load registry
      if (data.registry?.length > 0) {
        setRegistryType(data.registry[0].type || '');
        setRegistryCode(data.registry[0].code || '');
        setRegistryUf(data.registry[0].uf || '');
      }
      setBio(data.bio || '');
      setSpecialties(data.specialties || []);
      setApproaches(data.approaches || []);
      setModalities(data.modalities || []);
      setLanguages(data.languages || ['pt-BR']);
      setCity(data.city || '');
      setState(data.state || '');
      setCountry(data.country || 'BR');
      setPriceMin(data.price_min?.toString() || '');
      setPriceMax(data.price_max?.toString() || '');
      setYearsExperience(data.years_experience?.toString() || '');
      setWebsite(data.links?.website || '');
      setWhatsapp(data.links?.whatsapp || '');
      setInstagram(data.links?.instagram || '');
      setAvatarPreview(data.avatar_url);
    } catch (error) {
      // Profile doesn't exist yet, that's OK for first time
      if (error.response?.status !== 404) {
        console.error('Failed to load profile:', error);
        toast.error('Erro ao carregar perfil');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Formato inválido. Use JPG ou PNG');
      return;
    }

    if (file.size > 3 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo 3MB');
      return;
    }

    setAvatarFile(file);
    const preview = URL.createObjectURL(file);
    setAvatarPreview(preview);
  };

  const removeAvatar = () => {
    if (avatarPreview && avatarFile) {
      URL.revokeObjectURL(avatarPreview);
    }
    setAvatarFile(null);
    setAvatarPreview(profile?.avatar_url || null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const toggleSpecialty = (specialty) => {
    setSpecialties(prev =>
      prev.includes(specialty)
        ? prev.filter(s => s !== specialty)
        : [...prev, specialty]
    );
  };

  const toggleApproach = (approach) => {
    setApproaches(prev => {
      if (prev.includes(approach)) return prev.filter(a => a !== approach);
      if (prev.length >= 5) {
        toast.error('Máximo de 5 abordagens');
        return prev;
      }
      return [...prev, approach];
    });
  };

  const toggleModality = (modality) => {
    setModalities(prev =>
      prev.includes(modality)
        ? prev.filter(m => m !== modality)
        : [...prev, modality]
    );
  };

  const validateForm = () => {
    const newErrors = {};

    if (!displayName.trim() || displayName.length < 3) {
      newErrors.displayName = 'Nome deve ter pelo menos 3 caracteres';
    }

    if (!profession) {
      newErrors.profession = 'Selecione sua profissão';
    }

    if (!bio.trim() || bio.length < 10) {
      newErrors.bio = 'Bio deve ter pelo menos 10 caracteres';
    }

    if (bio.length > 500) {
      newErrors.bio = 'Bio não pode ter mais de 500 caracteres';
    }

    if (specialties.length === 0) {
      newErrors.specialties = 'Selecione pelo menos uma especialidade';
    }

    if (modalities.length === 0) {
      newErrors.modalities = 'Selecione pelo menos uma modalidade';
    }

    if (!city.trim()) {
      newErrors.city = 'Cidade é obrigatória';
    }

    if (!state.trim()) {
      newErrors.state = 'Estado é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Por favor, corrija os erros no formulário');
      return;
    }

    setSaving(true);
    try {
      const profileData = {
        display_name: displayName.trim(),
        profession,
        bio: bio.trim(),
        specialties,
        modalities,
        city: city.trim(),
        state: state.trim(),
        country,
        price_min: priceMin ? parseInt(priceMin) : null,
        price_max: priceMax ? parseInt(priceMax) : null,
        session_lengths_min: [50],
        languages,
        registry: registryCode ? [{ type: registryType || 'OTHER', code: registryCode, uf: registryUf || null }] : [],
        years_experience: yearsExperience ? parseInt(yearsExperience) : null,
        qualities: [],
        approaches,
        links: {
          website: website.trim() || null,
          whatsapp: whatsapp.trim() || null,
          instagram: instagram.trim() || null,
        },
        supervision_enabled: false,
      };

      // Create or update profile
      if (profile) {
        await professionalsAPI.updateProfile(profileData);
      } else {
        await professionalsAPI.createProfile(profileData);
      }

      // Upload avatar if changed
      if (avatarFile) {
        await professionalsAPI.uploadAvatar(avatarFile);
      }

      // Refresh user to update professional_enabled
      await refreshUser();

      toast.success(profile ? 'Perfil atualizado!' : 'Perfil profissional ativado!');
      
      // Reload profile
      await loadProfile();
    } catch (error) {
      console.error('Failed to save profile:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar perfil');
    } finally {
      setSaving(false);
    }
  };

  const handlePause = async () => {
    try {
      await professionalsAPI.pauseProfile();
      toast.success('Perfil pausado');
      await loadProfile();
    } catch (error) {
      toast.error('Erro ao pausar perfil');
    }
  };

  const handleActivate = async () => {
    try {
      await professionalsAPI.activateProfile();
      toast.success('Perfil ativado');
      await loadProfile();
    } catch (error) {
      toast.error('Erro ao ativar perfil');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const bioRemaining = 500 - bio.length;

  return (
    <div className="max-w-4xl mx-auto py-6 px-4" data-testid="professional-profile-page">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold mb-2">Perfil Profissional</h1>
          <p className="text-muted-foreground">
            {profile ? 'Edite suas informações profissionais' : 'Crie seu perfil profissional'}
          </p>
        </div>
        {profile && (
          <div className="flex items-center gap-2">
            <Badge variant={profile.professional_status === 'active' ? 'default' : 'secondary'}>
              {profile.professional_status === 'active' ? 'Ativo' : 'Pausado'}
            </Badge>
            {profile.professional_status === 'active' ? (
              <Button variant="outline" size="sm" onClick={handlePause}>
                <Pause className="h-4 w-4 mr-2" />
                Pausar Perfil
              </Button>
            ) : (
              <Button variant="outline" size="sm" onClick={handleActivate}>
                <Play className="h-4 w-4 mr-2" />
                Reativar Perfil
              </Button>
            )}
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        {/* Avatar */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Foto de Perfil</CardTitle>
            <CardDescription>Escolha uma foto profissional (JPG ou PNG, máx 3MB)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              {avatarPreview ? (
                <div className="relative">
                  <img
                    src={avatarPreview}
                    alt="Avatar"
                    className="w-32 h-32 rounded-full object-cover"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute top-0 right-0"
                    onClick={removeAvatar}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="w-32 h-32 rounded-full bg-muted flex items-center justify-center">
                  <Upload className="h-8 w-8 text-muted-foreground" />
                </div>
              )}
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/jpg"
                  onChange={handleAvatarChange}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Escolher Foto
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Basic Info */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Informações Básicas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="displayName">Nome Profissional *</Label>
              <Input
                id="displayName"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Dr(a). Seu Nome"
                aria-invalid={!!errors.displayName}
              />
              {errors.displayName && (
                <p className="text-sm text-red-500 mt-1">{errors.displayName}</p>
              )}
            </div>

            {/* Profession */}
            <div data-testid="profession-field">
              <Label htmlFor="profession">Profissão *</Label>
              <Select value={profession} onValueChange={setProfession}>
                <SelectTrigger id="profession" data-testid="profession-select">
                  <SelectValue placeholder="Selecione sua profissão" />
                </SelectTrigger>
                <SelectContent>
                  {PROFESSIONS.map(p => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.profession && (
                <p className="text-sm text-red-500 mt-1">{errors.profession}</p>
              )}
            </div>

            {/* Professional Registry */}
            <div className="space-y-3" data-testid="registry-field">
              <Label>Registro Profissional (CRP, CRM, CRT, etc.)</Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Select value={registryType} onValueChange={setRegistryType}>
                  <SelectTrigger data-testid="registry-type-select">
                    <SelectValue placeholder="Tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {REGISTRY_TYPES.map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  placeholder="Nº do Registro"
                  value={registryCode}
                  onChange={(e) => setRegistryCode(e.target.value)}
                  data-testid="registry-code-input"
                />
                <Input
                  placeholder="UF (ex: SP)"
                  value={registryUf}
                  onChange={(e) => setRegistryUf(e.target.value.toUpperCase().slice(0, 2))}
                  maxLength={2}
                  data-testid="registry-uf-input"
                />
              </div>
              <p className="text-xs text-muted-foreground">Opcional. Será exibido no seu perfil público.</p>
            </div>

            <div>
              <Label htmlFor="bio">Sobre Você *</Label>
              <Textarea
                id="bio"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Descreva sua experiência e abordagem..."
                className="min-h-[120px]"
                aria-invalid={!!errors.bio}
              />
              <div className="flex justify-between mt-1">
                {errors.bio && <p className="text-sm text-red-500">{errors.bio}</p>}
                <p className={`text-sm ml-auto ${bioRemaining < 50 ? 'text-yellow-600' : 'text-muted-foreground'}`}>
                  {bioRemaining} caracteres restantes
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Specialties & Approaches */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Especialidades e Abordagens</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Especialidades *</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {SPECIALTIES.map(specialty => (
                  <Badge
                    key={specialty}
                    variant={specialties.includes(specialty) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => toggleSpecialty(specialty)}
                  >
                    {specialty}
                  </Badge>
                ))}
              </div>
              {errors.specialties && (
                <p className="text-sm text-red-500 mt-1">{errors.specialties}</p>
              )}
            </div>

            <div>
              <Label>Abordagens Terapêuticas (máx. 5)</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Selecionadas: {approaches.length}/5
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                {APPROACHES.map(approach => (
                  <Badge
                    key={approach}
                    variant={approaches.includes(approach) ? 'default' : 'outline'}
                    className={`cursor-pointer ${
                      !approaches.includes(approach) && approaches.length >= 5
                        ? 'opacity-40 cursor-not-allowed'
                        : ''
                    }`}
                    onClick={() => toggleApproach(approach)}
                    data-testid={`approach-${approach}`}
                  >
                    {approach}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Modalities & Languages */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Modalidades e Idiomas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Modalidades de Atendimento *</Label>
              <div className="flex gap-2 mt-2">
                {MODALITIES.map(({ value, label }) => (
                  <Badge
                    key={value}
                    variant={modalities.includes(value) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => toggleModality(value)}
                  >
                    {label}
                  </Badge>
                ))}
              </div>
              {errors.modalities && (
                <p className="text-sm text-red-500 mt-1">{errors.modalities}</p>
              )}
            </div>

            <div>
              <Label>Idiomas de Atendimento</Label>
              <div className="flex gap-2 mt-2">
                {LANGUAGES.map(({ value, label }) => (
                  <Badge
                    key={value}
                    variant={languages.includes(value) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => {
                      setLanguages(prev =>
                        prev.includes(value) ? prev.filter(l => l !== value) : [...prev, value]
                      );
                    }}
                  >
                    {label}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Location & Price */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Localização e Preços</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city">Cidade *</Label>
                <Input
                  id="city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="São Paulo"
                  aria-invalid={!!errors.city}
                />
                {errors.city && <p className="text-sm text-red-500 mt-1">{errors.city}</p>}
              </div>
              <div>
                <Label htmlFor="state">Estado *</Label>
                <Input
                  id="state"
                  value={state}
                  onChange={(e) => setState(e.target.value.toUpperCase())}
                  placeholder="SP"
                  maxLength={2}
                  aria-invalid={!!errors.state}
                />
                {errors.state && <p className="text-sm text-red-500 mt-1">{errors.state}</p>}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="priceMin">Preço Mínimo (R$)</Label>
                <Input
                  id="priceMin"
                  type="number"
                  value={priceMin}
                  onChange={(e) => setPriceMin(e.target.value)}
                  placeholder="150"
                />
              </div>
              <div>
                <Label htmlFor="priceMax">Preço Máximo (R$)</Label>
                <Input
                  id="priceMax"
                  type="number"
                  value={priceMax}
                  onChange={(e) => setPriceMax(e.target.value)}
                  placeholder="300"
                />
              </div>
              <div>
                <Label htmlFor="yearsExperience">Anos de Experiência</Label>
                <Input
                  id="yearsExperience"
                  type="number"
                  value={yearsExperience}
                  onChange={(e) => setYearsExperience(e.target.value)}
                  placeholder="5"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact Links */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Contato e Redes Sociais</CardTitle>
            <CardDescription>Links para facilitar o contato com clientes</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="website">Site/Blog</Label>
              <Input
                id="website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="https://seusite.com"
              />
            </div>
            <div>
              <Label htmlFor="whatsapp">WhatsApp</Label>
              <Input
                id="whatsapp"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
                placeholder="https://wa.me/5511999999999"
              />
            </div>
            <div>
              <Label htmlFor="instagram">Instagram</Label>
              <Input
                id="instagram"
                value={instagram}
                onChange={(e) => setInstagram(e.target.value)}
                placeholder="https://instagram.com/seuperfil"
              />
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/feed')}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button type="submit" disabled={saving} className="flex-1">
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : profile ? (
              'Salvar Alterações'
            ) : (
              'Ativar Perfil Profissional'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
